/**
 * useTokenRefresh Hook
 * Managed automatischen Token-Refresh während der App-Laufzeit
 * Teil des Hybrid-Ansatzes für maximale Zuverlässigkeit
 */

import { useEffect } from 'react'
import { authService } from '@/services/auth-service'
import { logInfo, logWarn } from '@/lib/logger'

const CONTEXT = 'useTokenRefresh'
const CHECK_INTERVAL_MS = 60 * 1000 // Prüfe jede Minute

/**
 * Hook für automatischen Token-Refresh
 * Prüft regelmäßig ob Token bald abläuft und erneuert ihn
 */
export function useTokenRefresh() {
  useEffect(() => {
    logInfo({ context: CONTEXT }, 'Token refresh monitoring started')

    const renewIfLapsed = async (trigger: string) => {
      if (authService.getAccessToken()) return

      logInfo({ context: CONTEXT, trigger }, 'No valid token found, attempting refresh')
      const newToken = await authService.refreshAccessToken()

      if (newToken) {
        logInfo({ context: CONTEXT, trigger }, 'Token successfully refreshed')
      } else {
        // Event wird bereits von refreshAccessToken() gefeuert, nichts weiter zu tun
        logWarn({ context: CONTEXT, trigger }, 'Token refresh failed - auth-session-expired event will be fired by auth service')
      }
    }

    // Periodische Token-Prüfung
    const intervalId = window.setInterval(() => void renewIfLapsed('interval'), CHECK_INTERVAL_MS)

    // Im Hintergrund-Tab wird das Intervall gedrosselt und im Ruhezustand angehalten —
    // beim Zurückkommen sofort prüfen, nicht erst beim nächsten Tick (#1227).
    const onVisible = () => {
      if (document.visibilityState === 'visible') void renewIfLapsed('visible')
    }
    document.addEventListener('visibilitychange', onVisible)

    // Cleanup
    return () => {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
      logInfo({ context: CONTEXT }, 'Token refresh monitoring stopped')
    }
  }, [])
}
